import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useBeforeUnload } from '../../../../hooks';
import { Error, Field } from '../../../../components/Form';
import { Modal, onHideHandler, renderShowConfirmationModal } from '../../../../components';
import { InfiniteComboBox } from '../../../../containers';
import { actions as countryActions, selectors as countrySelectors } from '../../../../redux/countries';
import { actions as customerActions } from '../../../../redux/customers';
import { makePatch, makeGet } from '../../../../api/ajax';

const buildPayload = (values, initialValues, timeZones) => {
  const payload = {};
  const countryChanged = values.countryId !== initialValues.countryId;

  if (countryChanged) {
    payload.countryId = values.countryId;

    const defaultTimeZone = _.find(timeZones, 'isDefault');
    if (values.timeZoneId && values.timeZoneId !== defaultTimeZone?.timeZoneId) {
      payload.timeZoneId = values.timeZoneId;
    }
  } else if (values.timeZoneId !== initialValues.timeZoneId) {
    payload.timeZoneId = values.timeZoneId;
  }

  return payload;
};

const CountryModal = ({ onModalClose, country, customerDetails, ...props }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [timeZones, setTimeZones] = useState(country?.availableTimeZoneIds || []);

  const dispatch = useDispatch();

  useBeforeUnload(hasUnsavedChanges);

  const initialValues = {
    countryId: customerDetails?.countryId || '',
    timeZoneId: customerDetails?.timeZoneId || '',
  };

  const handleSubmit = (values, setStatus, resetForm) => {
    const data = buildPayload(values, initialValues, timeZones);

    makePatch('customers', data).subscribe(
      ({ response }) => {
        dispatch(customerActions.loadCustomerDetailsSuccess(response));

        makeGet('customers/current').subscribe(({ response: currentCustomer }) => {
          dispatch(customerActions.loadCustomerSuccess(currentCustomer));
        });

        setShowSuccessMessage('Country has been changed!');
        resetForm();

        setHasUnsavedChanges(false);
      },
      ({ response: { error } }) => {
        setStatus(error);

        setHasUnsavedChanges(false);
      },
    );
  };

  const hideDiscardConfirmation = () => {
    onModalClose();
    setShowConfirmModal(false);
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, hideDiscardConfirmation)}
      <Modal
        headerText="Change country / time zone"
        size="md"
        backdrop="static"
        onHide={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
        {...props}
      >
        <Modal.Body>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <Alert variant="success" dismissible onClick={() => setShowSuccessMessage(null)}>
                  {showSuccessMessage}
                </Alert>
              )}
            </div>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              countryId: Yup.string().required('Country is required'),
              timeZoneId: Yup.string().required('Time zone is required'),
            })}
            onSubmit={(values, { setStatus, resetForm }) => handleSubmit(values, setStatus, resetForm)}
            validateOnChange={false}
          >
            {({ errors, status, touched, setFieldValue }) => (
              <Form
                onChange={({ target }) => {
                  if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                <div className="row">
                  <div className="col-12 col-md-12">
                    <InfiniteComboBox
                      label="Country"
                      inputId="countryInput"
                      loadUrl="countries"
                      searchParam="countryName"
                      pageSize={300}
                      isInternalFiltration
                      disableInfiniteScroll
                      initialValue={country?.name}
                      onLoadSuccess={(countriesPaged) => dispatch(countryActions.loadCountriesSuccess(countriesPaged))}
                      resultSelector={countrySelectors.selectPagedResult}
                      isError={errors.countryId && true}
                      error={errors.countryId}
                      searchingDisabled={false}
                      placeholder="Search"
                      onSelect={(selectedCountry) => {
                        const countryId = _.isEmpty(selectedCountry) ? '' : selectedCountry.id;

                        setFieldValue('countryId', countryId, true);

                        const availableTimeZones = _.isEmpty(selectedCountry)
                          ? []
                          : selectedCountry.availableTimeZoneIds || [];

                        setTimeZones(availableTimeZones);

                        const defaultTimeZone = _.find(availableTimeZones, 'isDefault');
                        setFieldValue('timeZoneId', defaultTimeZone ? defaultTimeZone.timeZoneId : '');
                      }}
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12 col-md-12">
                    {timeZones.length > 1 && (
                      <Field
                        name="timeZoneId"
                        type="select"
                        label="Time zone"
                        errors={errors.timeZoneId}
                        touched={touched.timeZoneId}
                        options={_.map(timeZones, (tz) => ({
                          name: `${tz.displayName} (UTC${tz.utcOffset})`,
                          value: tz.timeZoneId,
                        }))}
                      />
                    )}
                    {timeZones.length === 1 && (
                      <div className="form-group">
                        <label className="mb-0 mt-1">Time zone</label>
                        <div>{`${timeZones[0].displayName} (UTC${timeZones[0].utcOffset})`}</div>
                      </div>
                    )}
                    {timeZones.length === 0 && errors.timeZoneId && <Error>{errors.timeZoneId}</Error>}
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12 col-md-12">{status && <Error>{status}</Error>}</div>
                </div>
                <hr />
                <div className="row">
                  <div className="col-12 col-md-12 text-right">
                    <button
                      type="reset"
                      className="btn btn-secondary mr-1"
                      onClick={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

CountryModal.propTypes = {
  onModalClose: PropTypes.func,
  country: PropTypes.object,
  customerDetails: PropTypes.object,
};

export default CountryModal;
